import { ControlsBaseComponent } from '../shared/components/controls-base.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { JsonDynamicComponentService } from '../json-dynamic-component.service';
import { CommonUtilsService } from '../shared/common-utils.service';
import { Config } from '../models/ui-config';
import { Source } from '../enums/source.enum';
import { cloneDeep, forOwn, size } from 'lodash';
import { filterBySearchBarQuery } from '../shared/components/search-bar-field/search-bar-field.utils';

export interface ChipOption {
  value: any;
  label: string;
}

@Component({
  selector: 'ra-chip-control',
  templateUrl: './chip-control.component.html',
  styleUrls: ['./chip-control.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class ChipControlComponent
  extends ControlsBaseComponent
  implements OnInit, OnDestroy
{
  chipOptions: ChipOption[] = [];
  /** When `isMultiSelect` is false, chip listbox is single-select; default is multi. */
  isMultiple = true;
  showSpinner = false;
  id = CommonUtilsService.createGUID();
  /** Filters visible chip options when `enableChipOptionSearch` is set on config. */
  chipSearchTerm = '';

  /** Interpolates `hintLabel` with form value and `externalParams` (via CommonUtilsService). */
  get resolvedHintLabel(): string {
    const hl = this.currentConfig?.hintLabel?.trim();
    if (!hl) return '';
    return (
      this.commonUtils.getInterpolatedValue(hl, this.form?.value ?? {}) || ''
    );
  }

  get chipOptionSearchEnabled(): boolean {
    return !!this.currentConfig?.enableChipOptionSearch;
  }

  /** Options shown in the listbox; when search is on, filter by label but keep selected values visible. */
  get displayChipOptions(): ChipOption[] {
    if (!this.chipOptionSearchEnabled) {
      return this.chipOptions;
    }
    const selected = this.getCurrentSelectionArray();
    return filterBySearchBarQuery(
      this.chipOptions,
      this.chipSearchTerm,
      (o) => o.label,
      {
        alwaysInclude: (o) => selected.some((s) => s === o.value),
      },
    );
  }

  get isSelectAllDisabled(): boolean {
    if (this.currentConfig.loading || this.showSpinner) {
      return true;
    }
    const all = this.chipOptions.map((o) => o.value);
    if (all.length === 0) {
      return true;
    }
    const sel = this.getCurrentSelectionArray();
    return all.every((x) => sel.includes(x)) && sel.length === all.length;
  }

  get isClearDisabled(): boolean {
    if (this.currentConfig.loading || this.showSpinner) {
      return true;
    }
    return this.getCurrentSelectionArray().length === 0;
  }

  get isResetToDefaultsDisabled(): boolean {
    if (this.currentConfig.loading || this.showSpinner) {
      return true;
    }
    const def = this.computeDefaultChipValue();
    const cur = this.parentFormGroup?.get(this.currentConfig.property)?.value;
    if (this.isMultiple) {
      return this.arraysEqualOrderInsensitive(
        this.normalizeToComparableArray(cur),
        this.normalizeToComparableArray(def),
      );
    }
    return cur === def || (cur == null && def == null);
  }

  constructor(
    protected jsonDynamicComponentService: JsonDynamicComponentService,
    protected commonUtils: CommonUtilsService,
  ) {
    super(jsonDynamicComponentService, commonUtils);
  }

  ngOnInit(): void {
    this.isMultiple = this.currentConfig.isMultiSelect !== false;
    this.currentConfig.minNumOfOptionsToShow =
      this.currentConfig.minNumOfOptionsToShow || Infinity;
    this.jsonDynamicComponentService.addChipControl(this.id, this);
    this.jsonDynamicComponentService.addChipControlInitialValues(this);

    this.normalizeControlValue();

    const propCtrl = this.parentFormGroup.get(this.currentConfig.property);
    const hasInitContext =
      (propCtrl?.value != null &&
        (!Array.isArray(propCtrl.value) || propCtrl.value.length > 0)) ||
      !!this.currentConfig.selectAllOnInit;

    if (hasInitContext) {
      if (!this.currentConfig.values) {
        this.loadChipOptionsFromSource(true);
      } else {
        this.buildChipOptions();
        this.selectAllValuesIfNeeded(this.currentConfig);
      }
    } else if (this.shouldEagerLoadRemoteOptions()) {
      this.loadChipOptionsFromSource(false);
    } else {
      this.buildChipOptions();
    }

    const control = this.parentFormGroup?.get(this.currentConfig?.property);
    if (
      this.currentConfig?.validators?.required &&
      control &&
      !(control as FormControl).hasValidator?.(Validators.required)
    ) {
      control.addValidators(Validators.required);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.jsonDynamicComponentService.removeChipControl(this.id);
    this.jsonDynamicComponentService.removeChipControlInitialValues(this);
  }

  /** Reload options and optionally re-apply select-all; used when dependent fields change (same as dropdown). */
  updateAndSelectAll(config: Config): void {
    this.commonUtils
      .getDropdownValues(config, this.form?.value ?? {}, undefined)
      .then(() => {
        this.buildChipOptions();
        if (config.isMultiSelect !== false && config.selectAllOnInit) {
          this.parentFormGroup
            .get(config.property)
            ?.setValue(this.getChipOptionFormValues(), { emitEvent: false });
        }
      });
  }

  validateSelectedValues(
    config: Config,
    configFilter: Function,
    hasModelChanged: boolean,
  ): Promise<boolean> {
    if (!configFilter(config)) {
      return Promise.resolve(true);
    }
    const isMulti = config.isMultiSelect !== false;
    return new Promise<boolean>((resolve) => {
      this.showSpinner = true;
      const control = this.parentFormGroup.get(config.property);
      let currentValues = control?.value;
      if (currentValues == null) {
        currentValues =
          this.jsonDynamicComponentService.chipControlsInitialValues[this.id];
      }
      if (isMulti && currentValues != null && !Array.isArray(currentValues)) {
        currentValues = [currentValues];
      }
      this.commonUtils
        .getDropdownValues(
          config,
          this.form?.value ?? {},
          undefined,
          hasModelChanged,
        )
        .then(() => {
          this.buildChipOptions();
          /* Do not use config.values for membership or select-all: when isGrouped, values are
           * group rows { label, options }[], not form values. Use the same flat list as chipOptions. */
          const validSelection = this.getValidChipSelectionSet(config);
          if (isMulti) {
            currentValues = (currentValues || []).filter((x) =>
              validSelection.has(x),
            );
          } else {
            currentValues = validSelection.has(currentValues)
              ? currentValues
              : null;
          }
          this.parentFormGroup
            .get(config.property)
            ?.setValue(currentValues, { emitEvent: false });
          if (isMulti && config.selectAllOnInit) {
            this.parentFormGroup
              .get(config.property)
              ?.setValue(this.getChipOptionFormValues(), { emitEvent: false });
          }
          this.showSpinner = false;
          resolve(true);
        })
        .catch(() => {
          this.showSpinner = false;
          resolve(false);
        });
    });
  }

  private shouldEagerLoadRemoteOptions(): boolean {
    const s = this.currentConfig.source;
    return s === Source.Url || s === Source.Function || s === Source.Dependent;
  }

  private loadChipOptionsFromSource(isInitCall: boolean): void {
    this.commonUtils
      .getDropdownValues(this.currentConfig, this.form?.value ?? {}, undefined)
      .then(() => {
        this.buildChipOptions();
        if (
          isInitCall &&
          this.currentConfig.isMultiSelect !== false &&
          this.currentConfig.selectAllOnInit &&
          size(this.parentFormGroup.get(this.currentConfig.property).value) ===
            0
        ) {
          this.selectAllValuesIfNeeded(this.currentConfig);
        }
        this.normalizeControlValue();
      });
  }

  private selectAllValuesIfNeeded(config: Config): void {
    if (config.isMultiSelect === false || !this.isMultiple) {
      return;
    }
    if (size(this.parentFormGroup.get(config.property).value)) {
      return;
    }
    this.parentFormGroup
      .get(config.property)
      .setValue(this.getChipOptionFormValues());
  }

  /** Form control values for every selectable chip (flat list; correct for grouped configs). */
  private getChipOptionFormValues(): any[] {
    return this.chipOptions.map((o) => o.value);
  }

  /** Values allowed on the control after options are built (chip values plus mapValueNames targets). */
  private getValidChipSelectionSet(config = this.currentConfig): Set<any> {
    return new Set([
      ...this.getChipOptionFormValues(),
      ...Object.values(config?.mapValueNames || []),
    ]);
  }

  private buildChipOptions(): void {
    const cfg = this.currentConfig;
    let flat: any[] = [];
    if (cfg?.isGrouped && Array.isArray(cfg.values)) {
      for (const g of cfg.values) {
        flat.push(...(g?.options ?? []));
      }
    } else {
      flat = cfg.values ?? [];
    }
    this.chipOptions = flat.map((v) => this.toChipOption(v));
  }

  private toChipOption(v: any): ChipOption {
    return {
      value: this.resolveOptionFormValue(v),
      label: this.resolveOptionLabel(v),
    };
  }

  private resolveOptionFormValue(option: any): any {
    const cfg = this.currentConfig;
    if (option == null) {
      return option;
    }
    if (cfg?.dropdownID) {
      return option[cfg.dropdownID];
    }
    if (!cfg?.optionDisplayFields?.length) {
      return option;
    }
    if (cfg?.isGrouped) {
      const f = cfg.optionDisplayFields;
      if (f.length > 1 && option[f[1]] != null && option[f[1]] !== undefined) {
        return `${option[f[0]]} (${option[f[1]]})`;
      }
      return option[f[0]];
    }
    return option[cfg.optionDisplayFields[0]];
  }

  private resolveOptionLabel(option: any): string {
    const cfg = this.currentConfig;
    if (option == null) {
      return '';
    }
    if (
      typeof option === 'string' ||
      typeof option === 'number' ||
      typeof option === 'boolean'
    ) {
      const primitiveKey = String(option);
      return cfg?.mapValueNames?.[primitiveKey] != null
        ? String(cfg.mapValueNames[primitiveKey])
        : String(option);
    }
    if (cfg?.dropdownLabel && option[cfg.dropdownLabel] != null) {
      return String(option[cfg.dropdownLabel]);
    }
    if (cfg?.optionDisplayFields?.length) {
      return cfg.optionDisplayFields
        .map((field) => option?.[field])
        .filter((x) => x != null)
        .join(' ');
    }
    return String(option);
  }

  private getCurrentSelectionArray(): any[] {
    const v = this.parentFormGroup?.get(this.currentConfig.property)?.value;
    if (this.isMultiple) {
      if (v == null) {
        return [];
      }
      return Array.isArray(v) ? [...v] : [v];
    }
    return v == null || v === '' ? [] : [v];
  }

  private normalizeToComparableArray(value: any): any[] {
    if (value == null) {
      return [];
    }
    if (Array.isArray(value)) {
      return [...value];
    }
    return [value];
  }

  private arraysEqualOrderInsensitive(a: any[], b: any[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const key = (x: any) =>
      typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean'
        ? String(x)
        : JSON.stringify(x);
    const sa = [...a].map(key).sort();
    const sb = [...b].map(key).sort();
    return sa.every((s, i) => s === sb[i]);
  }

  private normalizeControlValue(): void {
    const control = this.parentFormGroup?.get(this.currentConfig.property);
    if (!control) {
      return;
    }
    const v = control.value;
    if (this.isMultiple) {
      if (v == null || !Array.isArray(v)) {
        control.setValue(v == null ? [] : [v], { emitEvent: false });
      }
    } else if (Array.isArray(v)) {
      control.setValue(v.length ? v[0] : null, { emitEvent: false });
    }
  }

  onChipListChange(): void {
    const c = this.parentFormGroup?.get(this.currentConfig.property);
    c?.markAsDirty();
    c?.markAsTouched();
    this.onValueChanged();
  }

  onSelectAll(): void {
    if (this.readOnly || !this.isMultiple) {
      return;
    }
    const control = this.parentFormGroup?.get(this.currentConfig.property);
    if (!control) {
      return;
    }
    const all = this.chipOptions.map((o) => o.value);
    control.setValue([...all]);
    this.onChipListChange();
  }

  onClearAll(): void {
    if (this.readOnly) {
      return;
    }
    const control = this.parentFormGroup?.get(this.currentConfig.property);
    if (!control) {
      return;
    }
    if (this.isMultiple) {
      control.setValue([]);
    } else {
      control.setValue(null);
    }
    this.onChipListChange();
  }

  onResetToDefaults(): void {
    if (this.readOnly) {
      return;
    }
    const control = this.parentFormGroup?.get(this.currentConfig.property);
    if (!control) {
      return;
    }
    const next = this.computeDefaultChipValue();
    control.setValue(next);
    this.normalizeControlValue();
    this.onChipListChange();
  }

  /**
   * Matches JsonDynamicFormComponent.getPropertyValue when there is no saved value:
   * defaultValue / defaultValueConditionExpression, then [] for multi chips, else null.
   */
  private computeDefaultChipValue(): any {
    const config = this.currentConfig;
    const formValue = this.form?.value ?? {};
    let evaluatedDefaultValue: any;

    if (config.defaultValue) {
      if (typeof config.defaultValue === 'string') {
        try {
          evaluatedDefaultValue = JSON.parse(
            this.commonUtils.getInterpolatedValue(
              config.defaultValue,
              formValue,
            ),
          );
        } catch {
          evaluatedDefaultValue = null;
        }
      } else if (typeof config.defaultValue === 'boolean') {
        evaluatedDefaultValue = config.defaultValue;
      } else if (Array.isArray(config.defaultValue)) {
        const seen = new Set();
        evaluatedDefaultValue = [];
        for (const v of config.defaultValue) {
          if (!seen.has(v)) {
            seen.add(v);
            evaluatedDefaultValue.push(v);
          }
        }
      } else if (typeof config.defaultValue === 'object') {
        evaluatedDefaultValue = cloneDeep(config.defaultValue);
      }
    } else if (config.defaultValueConditionExpression) {
      forOwn(config.defaultValueConditionExpression, (conditionExpr, key) => {
        if (
          this.commonUtils.safeEvaluate(
            this.commonUtils.getInterpolatedValue(conditionExpr, formValue),
          )
        ) {
          evaluatedDefaultValue = key;
        }
      });
    }

    const defaultValue = evaluatedDefaultValue ?? null;

    if (this.isMultiple) {
      if (defaultValue != null) {
        return !(defaultValue instanceof Array)
          ? [defaultValue]
          : [...defaultValue];
      }
      return [];
    }

    return defaultValue;
  }
}
